import React from 'react';
import { X } from 'lucide-react';

interface TenantProfessionalProps {
  formData: any;
  customFieldName: string;
  setCustomFieldName: (name: string) => void;
  customFieldValue: string;
  setCustomFieldValue: (value: string) => void;
  handleConfigChange: (path: string, value: any) => void;
}

export const TenantProfessional: React.FC<TenantProfessionalProps> = ({
  formData,
  customFieldName,
  setCustomFieldName,
  customFieldValue,
  setCustomFieldValue,
  handleConfigChange,
}) => {
  return (
    <section>
      {/* Champs personnalisés */}
      <div className="mb-8">
        <h3 className="text-lg font-medium text-white mb-4">Champs personnalisés</h3>
        <p className="text-gray-400 mb-4">Ajoutez des champs personnalisés pour votre profil professionnel.</p>

        {/* Liste des champs personnalisés existants */}
        <div className="space-y-4 mb-6">
          {Object.entries(formData.config || {}).filter(([key]) =>
            key.startsWith('custom_')
          ).map(([key, value]) => (
            <div key={key} className="bg-white/5 p-4 rounded-lg">
              <div className="flex justify-between items-center mb-2">
                <div className="text-sm font-medium text-amber-400">{key.replace('custom_', '')}</div>
                <button
                  type="button"
                  onClick={async () => {
                    if (window.confirm('Êtes-vous sûr de vouloir supprimer définitivement ce champ ? Cette action est irréversible.')) {
                      const newConfig = {...formData.config};
                      delete newConfig[key];
                      // Note: This would need to be handled by parent component
                      // We need to pass a callback to handle this
                    }
                  }}
                  className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-400/10 rounded-md"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <input
                type="text"
                value={String(value)}
                onChange={(e) => handleConfigChange(key, e.target.value)}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
              />
            </div>
          ))}
        </div>

        {/* Formulaire d'ajout de champ personnalisé */}
        <div className="bg-white/5 p-4 rounded-lg">
          <h4 className="text-sm font-medium text-white mb-3">Ajouter un champ personnalisé</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input
              type="text"
              placeholder="Nom du champ"
              value={customFieldName || ''}
              onChange={(e) => setCustomFieldName(e.target.value)}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
            />
            <input
              type="text"
              placeholder="Valeur"
              value={customFieldValue || ''}
              onChange={(e) => setCustomFieldValue(e.target.value)}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
            />
          </div>
          <button
            type="button"
            onClick={() => {
              if (customFieldName && customFieldValue) {
                const fieldKey = `custom_${customFieldName.trim().replace(/\s+/g, '_').toLowerCase()}`;
                handleConfigChange(fieldKey, customFieldValue);
                setCustomFieldName('');
                setCustomFieldValue('');
              }
            }}
            disabled={!customFieldName || !customFieldValue}
            className="mt-3 px-4 py-2 bg-amber-600 hover:bg-amber-700 disabled:bg-gray-600 disabled:text-gray-400 text-white rounded-md transition-colors"
          >
            Ajouter
          </button>
        </div>
      </div>

      {/* Champs de configuration non définis explicitement */}
      <div>
        <h3 className="text-lg font-medium text-white mb-4">Champs de configuration existants</h3>
        <p className="text-gray-400 mb-4">Visualisez et modifiez tous les champs présents dans la configuration mais non définis explicitement dans l'interface.</p>

        {/* Liste des champs non définis explicitement */}
        <div className="space-y-4">
          {Object.entries(formData.config || {}).filter(([key]) => {
            // Exclure les champs définis explicitement dans l'interface et les champs personnalisés
            const definedFields = [
              'year', 'phone', 'phone2', 'phone3', 'icon',
              'country', 'city', 'district', 'street', 'address','country_code','bg',
              'opening_hours', 'latitude', 'longitude', 'google_maps_url', 'slogan',
              'social', 'color1', 'color2', 'color3', 'color4', 'email2'
            ];
            return !definedFields.includes(key) &&
                   !key.startsWith('custom_') &&
                   typeof key === 'string';
          }).map(([key, value]) => {
            // Ne pas afficher les objets complexes comme social
            if (typeof value === 'object' && value !== null) return null;

            return (
              <div key={key} className="bg-white/5 p-4 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <div className="text-sm font-medium text-amber-400">{key}</div>
                  <button
                    type="button"
                    onClick={async () => {
                      if (window.confirm('Êtes-vous sûr de vouloir supprimer définitivement ce champ ? Cette action est irréversible.')) {
                        const newConfig = {...formData.config};
                        delete newConfig[key];
                        // Note: This would need to be handled by parent component
                        // We need to pass a callback to handle this
                      }
                    }}
                    className="p-1.5 text-gray-400 hover:text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded-md transition-colors duration-200"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <input
                  type="text"
                  value={String(value)}
                  onChange={(e) => handleConfigChange(key, e.target.value)}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
                />
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
